<template>
  <div class="pa-4">
    <summary-pill-bar :pills="groupPills" :clickable="false" class="mb-3" />

    <v-row class="mb-2" dense>
      <v-col cols="12" sm="6" md="4">
        <text-field
          v-model="search"
          kind="search"
          clearable
          :placeholder="$t('contracts_search_placeholder')"
          hide-details
        />
      </v-col>
    </v-row>

    <!-- Desktop table -->
    <v-data-table
      v-if="!device.isMobile"
      :headers="headers"
      :items="filteredItems"
      :loading="loading"
      :loading-text="$t('loading_data')"
      sort-by="naziv"
      class="striped-table ordera-box-shadow"
      mobile-breakpoint="0"
    >
      <template v-slot:item.naziv="{ item }">
        <span class="font-weight-medium">{{ toLatin(item.naziv) }}</span>
      </template>

      <template v-slot:item.status="{ item }">
        <v-chip small :color="statusColor(item.status)" text-color="white">{{
          item.status
        }}</v-chip>
      </template>

      <template v-slot:item.price="{ item }">
        <template v-if="item.nacin_naplate || item.iznos">
          {{ item.nacin_naplate }}
          <template v-if="item.iznos">· {{ item.iznos }}</template>
        </template>
        <span v-else>—</span>
      </template>

      <template v-slot:item.vazi_od="{ item }">
        <template v-if="item.vazi_od">{{ localeDate(item.vazi_od) }}</template>
        <span v-else>—</span>
      </template>

      <template v-slot:item.actions="{ item }">
        <div class="text-end">
          <v-btn small outlined color="primary" @click="$emit('open-item', item)">
            {{ $t("open") }}
          </v-btn>
        </div>
      </template>

      <template v-slot:no-data>
        <empty-state icon="mdi-cash-check" :title="$t('nothing_pending')" />
      </template>
    </v-data-table>

    <!-- Mobile cards -->
    <mobile-data-cards
      v-else
      :headers="mobileHeaders"
      :items="filteredItems"
      :loading="loading"
      item-key="restaurant_id"
      :show-row-number="false"
      :fields-per-row="2"
      :bold-fields="['naziv']"
      empty-icon="mdi-cash-check"
      :empty-title="$t('nothing_pending')"
    >
      <template v-slot:item.naziv="{ item }">{{ toLatin(item.naziv) }}</template>
      <template v-slot:item.status="{ item }">
        <v-chip x-small :color="statusColor(item.status)" text-color="white">{{
          item.status
        }}</v-chip>
      </template>
      <template v-slot:item.price="{ item }">
        <template v-if="item.nacin_naplate || item.iznos">
          {{ item.nacin_naplate }}
          <template v-if="item.iznos">· {{ item.iznos }}</template>
        </template>
        <span v-else>—</span>
      </template>
      <template v-slot:item.vazi_od="{ item }">
        <template v-if="item.vazi_od">{{ localeDate(item.vazi_od) }}</template>
        <span v-else>—</span>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn small block outlined color="primary" @click="$emit('open-item', item)">
          {{ $t("open") }}
        </v-btn>
      </template>
    </mobile-data-cards>
  </div>
</template>

<script>
import SummaryPillBar from "./SummaryPillBar.vue";
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";

// Ordered roughly by how urgent/unresolved each status is:
// nothing entered yet or contract lapsed (error) → entered but incomplete or
// expiring soon (warning) → entered and just needs a confirmation click (info).
const PENDING_STATUS_COLORS = {
  "NEDOSTAJE CJENOVNIK": "error",
  "UGOVOR ISTEKAO": "error",
  "CJENOVNIK BEZ IZNOSA": "warning",
  "UGOVOR USKORO ISTIČE": "warning",
  "CJENOVNIK NIJE POTVRĐEN": "info",
};

export default {
  name: "PendingVerificationTab",
  components: { SummaryPillBar, EmptyState, MobileDataCards },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
    groups: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      search: "",
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t("restaurant"), value: "naziv", sortable: true },
        { text: this.$t("status"), value: "status", sortable: true, width: "220px" },
        { text: this.$t("price"), value: "price", sortable: false },
        { text: this.$t("price_valid_from"), value: "vazi_od", sortable: false, width: "160px" },
        { text: this.$t("actions"), value: "actions", sortable: false, align: "end" },
      ];
    },
    mobileHeaders() {
      return [
        { text: this.$t("restaurant"), value: "naziv", cols: 12 },
        { text: this.$t("status"), value: "status", cols: 12 },
        { text: this.$t("price"), value: "price", cols: 6 },
        { text: this.$t("price_valid_from"), value: "vazi_od", cols: 6 },
        { text: "", value: "actions", cols: 12 },
      ];
    },
    groupPills() {
      return Object.entries(this.groups).map(([group, count]) => ({
        key: group,
        label: group,
        count,
        color: this.statusColor(group),
      }));
    },
    filteredItems() {
      return this.customSearch(this.items, [{ value: "naziv" }, { value: "status" }], this.search);
    },
  },
  methods: {
    statusColor(status) {
      return PENDING_STATUS_COLORS[status] || "warning";
    },
  },
};
</script>
